/**
 * 前端显示逻辑引擎（Step 2 的 backend/src/lib/display-logic.mjs 的 ts 桥接）
 * 与后端逻辑保持一致；预览页和填卷页都用这个算"当前应显示哪些题"
 */

import type { SurveyConfig, DisplayLogic, Condition, Question, JumpTarget } from '../components/editor/types';
import { evaluateOptionLinks } from '../components/editor/types';

interface Ctx {
  answers: Record<string, unknown>;
  visibleIds: Set<string>;
}

/** 文本题答案配图：{ text, images } 对象取 text 参与条件求值 */
function answerText(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value) && 'text' in value) {
    return (value as { text?: unknown }).text;
  }
  return value;
}

const OPS: Record<string, (c: Condition, a: Record<string, unknown>) => boolean> = {
  equals(c, a) {
    const v = answerText(a[c.question]);
    if (v === undefined || v === null) return false;
    return v === c.value || String(v) === String(c.value);
  },
  contains(c, a) {
    const v = answerText(a[c.question]);
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) {
      return v.includes(c.value) || v.some((x) => String(x) === String(c.value));
    }
    if (typeof v === 'string') return v.includes(String(c.value));
    return false;
  },
  gt(c, a) {
    const v = a[c.question];
    if (typeof v !== 'number' || typeof c.value !== 'number') return false;
    return v > c.value;
  },
  lt(c, a) {
    const v = a[c.question];
    if (typeof v !== 'number' || typeof c.value !== 'number') return false;
    return v < c.value;
  },
  between(c, a) {
    const v = a[c.question];
    if (typeof v !== 'number') return false;
    if (!Array.isArray(c.value) || c.value.length !== 2) return false;
    const [mn, mx] = c.value as [number, number];
    if (typeof mn !== 'number' || typeof mx !== 'number') return false;
    return v >= mn && v <= mx;
  },
};

export function evaluateCondition(c: Condition, ctx: Ctx): boolean {
  if (!c || typeof c.question !== 'string') return false;
  if (ctx.visibleIds && !ctx.visibleIds.has(c.question)) return false;
  const op = OPS[c.op];
  if (!op) return false;
  return op(c, ctx.answers);
}

export function evaluateDisplayLogic(logic: DisplayLogic | undefined, ctx: Ctx): boolean {
  if (!logic || !Array.isArray(logic.conditions) || logic.conditions.length === 0) return true;
  const results = logic.conditions.map((c) => evaluateCondition(c, ctx));
  if (logic.operator === 'OR') return results.some(Boolean);
  return results.every(Boolean);
}

/**
 * 1627 题目关联：单条 = { questionId, optionValue[, row] }，多条 OR 关系。
 *  - 引用题不在 visibleIds → 该条视为 false
 *  - 选择题（单选/下拉/排序）：answer === optionValue
 *  - 选择题（多选）：answer.includes(optionValue)
 *  - 矩阵题（带 row）：answers[questionId][row] === optionValue
 */
export function evaluateCorrelation(
  correlation: { questionId: string; optionValue: string; row?: string }[] | undefined,
  answers: Record<string, unknown>,
  visibleIds?: Set<string>,
): boolean {
  if (!Array.isArray(correlation) || correlation.length === 0) return true;
  return correlation.some((c) => {
    if (!c || typeof c.questionId !== 'string' || typeof c.optionValue !== 'string') return false;
    if (visibleIds && !visibleIds.has(c.questionId)) return false;
    const ans = answers?.[c.questionId];
    if (ans === undefined || ans === null) return false;
    // 矩阵触发：答案为对象，取 ans[row]
    if (typeof c.row === 'string' && c.row !== '') {
      if (!ans || typeof ans !== 'object' || Array.isArray(ans)) return false;
      return String((ans as Record<string, unknown>)[c.row] ?? '') === c.optionValue;
    }
    if (Array.isArray(ans)) return ans.some((v) => String(v) === c.optionValue);
    return String(ans) === c.optionValue;
  });
}

export function computeVisibleQuestions(survey: SurveyConfig, answers: Record<string, unknown> = {}): string[] {
  if (!survey?.questions) return [];
  const visibleIds = new Set<string>();
  const ctx: Ctx = { answers, visibleIds };
  for (const q of survey.questions) {
    if (typeof q.id !== 'string') continue;
    // 1627 题目关联 + displayLogic + 0907 选项关联（无规则命中则整题不显示）必须同时通过才显示
    const corrOk = evaluateCorrelation(q.correlation, answers, visibleIds);
    if (corrOk && evaluateDisplayLogic(q.displayLogic, ctx) && evaluateOptionLinks(q, answers, visibleIds)) visibleIds.add(q.id);
  }
  return survey.questions.filter((q) => visibleIds.has(q.id)).map((q) => q.id);
}

/* ---------------- 0904 跳题逻辑 ---------------- */

/** 解析某题的跳转目标；无配置/答案未触发返回 null。优先级：unconditional > byOption */
export function resolveJumpTarget(q: Question | undefined, ans: unknown): JumpTarget | null {
  const jl = q?.jumpLogic;
  if (!jl || typeof jl !== 'object') return null;
  if (jl.unconditional && typeof jl.unconditional === 'object') return jl.unconditional;
  if (jl.byOption && typeof jl.byOption === 'object' && !Array.isArray(jl.byOption)) {
    if (Array.isArray(ans)) {
      // 多选题：按勾选顺序取第一个匹配的选项值
      for (const v of ans) {
        if (typeof v === 'string' && jl.byOption[v]) return jl.byOption[v];
      }
    } else if (typeof ans === 'string' && jl.byOption[ans]) {
      return jl.byOption[ans];
    }
  }
  return null;
}

/**
 * 计算问卷在给定 answers 下「实际遍历到的题号序列」。与后端 display-logic.mjs computeJumpPath 同规则。
 *
 * @returns {{ path: string[], skipped: Set<string>, endReason: null | 'end' | 'submitInvalid' | 'end-of-survey' }}
 */
export function computeJumpPath(survey: SurveyConfig, answers: Record<string, unknown> = {}): {
  path: string[];
  skipped: Set<string>;
  endReason: null | 'end' | 'submitInvalid' | 'end-of-survey';
} {
  const visibleIds = computeVisibleQuestions(survey, answers);
  const questionsById = new Map<string, Question>((survey?.questions ?? []).map((q) => [q.id, q]));

  const path: string[] = [];
  const skipped = new Set<string>();
  let endReason: null | 'end' | 'submitInvalid' | 'end-of-survey' = null;

  let i = 0;
  const maxIter = visibleIds.length * 2 + 4;
  for (let iter = 0; iter < maxIter && i < visibleIds.length; iter += 1) {
    const id = visibleIds[i];
    const q = questionsById.get(id);
    if (!q) { i += 1; continue; }

    // 标题说明 / 分页栏：作为页面分隔进入 path，不参与跳题
    if (q.type === 'statement' || q.type === 'pagebreak') {
      path.push(id);
      i += 1;
      continue;
    }

    path.push(id);

    const target = resolveJumpTarget(q, answers[id]);
    if (!target || target.kind === 'next') { i += 1; continue; }
    if (target.kind === 'end') { endReason = 'end'; break; }
    if (target.kind === 'submitInvalid') { endReason = 'submitInvalid'; break; }
    if (target.kind === 'question') {
      const targetIdx = visibleIds.indexOf(target.qid);
      if (targetIdx < 0) { i += 1; continue; }
      for (let j = i + 1; j < targetIdx; j += 1) skipped.add(visibleIds[j]);
      i = targetIdx;
      continue;
    }
    i += 1;
  }

  for (let j = i + 1; j < visibleIds.length; j += 1) {
    if (!path.includes(visibleIds[j])) skipped.add(visibleIds[j]);
  }

  if (!endReason && i >= visibleIds.length) endReason = 'end-of-survey';
  return { path, skipped, endReason };
}
